/**
 * AutomationRuntimeBridge
 *
 * Exposes automation execution runtime state (run history, live run) from the
 * React service layer (LoggingService / ExecutionService) to Lit canvas
 * blocks, following the `__databaseBridge` pattern
 * (see use-database-discovery-bridge.ts).
 *
 * Local-first contract: canvas blocks render persisted Y.js props first and
 * treat this bridge as an additive overlay. When the bridge is absent or the
 * backend is offline, the last known in-memory state stays in place — no
 * loading spinners, no layout shift.
 */

import { notify } from '@affine/component';
import { I18n } from '@affine/i18n';
import type { ReadinessFacts } from '@streamlain/automation-safety-contract';
import {
  type CleanupRunResult,
  readCleanupRunResult,
} from '@streamlain/database-identity';

import {
  canAttemptBackendRequest,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import { formatWorkflowErrorMessage } from '../utils/workflow-error-message';
import type {
  AutomationConfigApiService,
  ConfigReadResponse,
  ScheduleResponse,
} from './automation-config-api';
import type { ExecutionService, ExecutionState } from './execution';
import type { ExecutionLog, LoggingService } from './logging';

// =============================================================================
// Public snapshot types (consumed by the Lit canvas block)
// =============================================================================

export interface AutomationRunSummary {
  id: string;
  status: 'success' | 'error' | 'cancelled' | 'running';
  startedAt: number;
  completedAt?: number;
  durationMs?: number;
  error?: string;
  /** Set when loop protection stopped the run. */
  terminalReason?: ExecutionLog['terminalReason'];
  /** Number of workflow node steps in this run (from process stage) */
  stepCount?: number;
  /** Rows passed into the workflow during this run. */
  inputRows?: number;
  rowsWritten?: number;
  cleanup?: CleanupRunResult;
}

export interface AutomationActiveRun {
  startedAt: number;
  stepName?: string;
  /** 1-based index of the currently running workflow step */
  stepIndex?: number;
  stepCount?: number;
  /** 0-100 overall progress if known */
  percent?: number;
}

export interface AutomationRuntimeSnapshot {
  /** Completed runs, newest first (capped by LoggingService history size) */
  history: AutomationRunSummary[];
  /** Currently running execution, if any */
  activeRun: AutomationActiveRun | null;
  /** Backend-owned schedule state. Never derive this from the browser clock. */
  schedule: AutomationScheduleSnapshot | null;
  /**
   * The readiness facts the sidebar loaded for this automation (workflow,
   * schema, AI keys, safety); `null` until the sidebar has shown it.
   */
  readinessFacts: AutomationReadinessFacts | null;
  /** Who it runs as and which steps the reader may use; null until read. */
  access: AutomationAccessSnapshot | null;
  /** New rows the import brake holds back; 0 until read. */
  heldRows: number;
}

/** Who an automation runs as and, per step with a connection, the reader's use. */
export interface AutomationAccessSnapshot {
  runsAs: {
    name: string | null;
    avatarUrl: string | null;
    /** The reader is the person it runs as. */
    isReader: boolean;
  } | null;
  /** Step ID → whether the reader may use its credentials. */
  steps: Record<string, 'usable' | 'missing'>;
}

/** The facts and the workflow they were loaded for. */
export interface AutomationReadinessFacts extends ReadinessFacts {
  workflowId: string | null;
}

export interface AutomationScheduleSnapshot {
  enabled: boolean;
  /** Why the server switched it off; null when on or switched off by a person. */
  pausedReason: string | null;
  lastRunAt: number | null;
  nextRunAt: number | null;
}

export interface AutomationRuntimeUpdateDetail {
  blockId: string;
}

declare global {
  interface Window {
    /** Runtime state bridge for Lit automation canvas blocks */
    __automationRuntimeBridge?: {
      getRuntime: (blockId: string) => AutomationRuntimeSnapshot | null;
      /**
       * Attach live subscriptions for a block and lazily fetch its
       * persisted history from the backend (once per session, deferred
       * while the backend is unavailable).
       */
      ensureRuntime: (blockId: string, docId?: string) => void;
      releaseRuntime: (blockId: string) => void;
      refreshSchedule: (blockId: string, docId: string) => void;
      setSchedule: (blockId: string, schedule: ScheduleResponse | null) => void;
      setReadinessFacts: (
        blockId: string,
        facts: AutomationReadinessFacts
      ) => void;
      resolveHeldRows: (
        blockId: string,
        docId: string,
        action: 'run' | 'discard'
      ) => Promise<void>;
    };
  }
  interface WindowEventMap {
    'automation-runtime-updated': CustomEvent<AutomationRuntimeUpdateDetail>;
    'automation-runtime-ready': CustomEvent<void>;
  }
}

// =============================================================================
// Module state
// =============================================================================

interface RuntimeSource {
  configApi: AutomationConfigApiService;
  logging: LoggingService;
  execution: ExecutionService;
  getWorkspaceId: () => string;
  getReaderId: () => string | null;
}

let source: RuntimeSource | null = null;

const blockSubscriptions = new Map<
  string,
  Array<{ unsubscribe: () => void }>
>();
const historyRequested = new Set<string>();
const pendingHistory = new Set<string>();
const schedules = new Map<string, AutomationScheduleSnapshot | null>();
const scheduleDocIds = new Map<string, string>();
const readinessFacts = new Map<string, AutomationReadinessFacts>();
const accesses = new Map<string, AutomationAccessSnapshot | null>();
const heldRowCounts = new Map<string, number>();
const scheduleRequests = new Map<string, Promise<void>>();
let availabilityUnsubscribe: (() => void) | null = null;

// =============================================================================
// Mapping
// =============================================================================

function mapLogStatus(
  status: ExecutionLog['status']
): AutomationRunSummary['status'] {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'cancelled';
    case 'running':
    case 'awaiting_external':
      return 'running';
  }
}

function mapLog(log: ExecutionLog): AutomationRunSummary {
  const durationMs =
    log.completedAt !== undefined
      ? Math.max(0, log.completedAt - log.startedAt)
      : undefined;
  const error = log.error ?? log.process?.error;
  return {
    id: log.id,
    status: mapLogStatus(log.status),
    startedAt: log.startedAt,
    completedAt: log.completedAt,
    durationMs,
    // Canvas blocks show this text as it is; an error that carries a stable
    // payload reaches them in the app language, like in the history.
    error: error ? (formatWorkflowErrorMessage(I18n, error) ?? error) : error,
    terminalReason: log.terminalReason,
    stepCount: log.process?.nodeSteps?.length,
    inputRows: log.input?.passedFilter,
    rowsWritten: log.output?.rowsWritten,
    cleanup: readCleanupRunResult(log.process?.response?.cleanup) ?? undefined,
  };
}

function buildActiveRun(
  activeLog: ExecutionLog | null,
  state: ExecutionState
): AutomationActiveRun | null {
  if (!activeLog) return null;

  const steps = activeLog.process?.nodeSteps;
  const runningIndex = steps?.findIndex(step => step.status === 'running');
  const hasRunningStep = runningIndex !== undefined && runningIndex >= 0;

  return {
    startedAt: activeLog.startedAt,
    stepName:
      state.progress.media?.stepName ??
      (hasRunningStep ? steps?.[runningIndex]?.name : undefined),
    stepIndex: hasRunningStep ? runningIndex + 1 : undefined,
    stepCount: steps?.length,
    percent:
      state.status === 'running' || state.status === 'queued'
        ? state.progress.percentage
        : undefined,
  };
}

// =============================================================================
// Subscriptions & history fetch
// =============================================================================

function dispatchUpdate(blockId: string): void {
  window.dispatchEvent(
    new CustomEvent<AutomationRuntimeUpdateDetail>(
      'automation-runtime-updated',
      { detail: { blockId } }
    )
  );
}

export function toAutomationScheduleSnapshot(
  schedule: ScheduleResponse | null
): AutomationScheduleSnapshot | null {
  if (!schedule) return null;
  const lastRunAt = schedule.lastRunAt ? Date.parse(schedule.lastRunAt) : NaN;
  const nextRunAt = schedule.nextRunAt ? Date.parse(schedule.nextRunAt) : NaN;
  return {
    enabled: schedule.enabled,
    pausedReason: schedule.pausedReason,
    lastRunAt: Number.isFinite(lastRunAt) ? lastRunAt : null,
    nextRunAt: Number.isFinite(nextRunAt) ? nextRunAt : null,
  };
}

export function toAutomationAccessSnapshot(
  read: ConfigReadResponse | null,
  readerId: string | null
): AutomationAccessSnapshot | null {
  if (!read) return null;
  const steps: Record<string, 'usable' | 'missing'> = {};
  for (const entry of read.connections) {
    if (!entry.usable) steps[entry.stepId] = 'missing';
    else steps[entry.stepId] ??= 'usable';
  }
  return {
    runsAs: read.runsAs
      ? {
          name: read.runsAs.name,
          avatarUrl: read.runsAs.avatarUrl,
          isReader: read.runsAs.userId === readerId,
        }
      : null,
    steps,
  };
}

export function resolveRefreshedAutomationSchedule(
  current: AutomationScheduleSnapshot | null | undefined,
  schedule: ScheduleResponse | null | undefined
): AutomationScheduleSnapshot | null {
  const refreshed = toAutomationScheduleSnapshot(schedule ?? null);
  // A config refresh can briefly observe the config before its joined schedule
  // row is visible. Keep the last backend-owned projection through that
  // transient boundary; explicit enable/disable writes still use setSchedule
  // below and therefore update immediately.
  return refreshed ?? current ?? null;
}

export function setAutomationSchedule(
  blockId: string,
  schedule: ScheduleResponse | null
): void {
  schedules.set(blockId, toAutomationScheduleSnapshot(schedule));
  dispatchUpdate(blockId);
}

export function setAutomationReadinessFacts(
  blockId: string,
  facts: AutomationReadinessFacts
): void {
  readinessFacts.set(blockId, facts);
  dispatchUpdate(blockId);
}

export function refreshAutomationSchedule(
  blockId: string,
  docId: string
): void {
  if (!source || scheduleRequests.has(blockId)) return;
  scheduleDocIds.set(blockId, docId);
  const request = source.configApi
    .getConfig(blockId, source.getWorkspaceId(), docId)
    .then(config => {
      schedules.set(
        blockId,
        resolveRefreshedAutomationSchedule(
          schedules.get(blockId),
          config?.schedule
        )
      );
      accesses.set(
        blockId,
        toAutomationAccessSnapshot(config, source?.getReaderId() ?? null)
      );
      heldRowCounts.set(blockId, config?.heldRows ?? 0);
      dispatchUpdate(blockId);
    })
    .finally(() => {
      scheduleRequests.delete(blockId);
    });
  scheduleRequests.set(blockId, request);
}

function attachBlockSubscriptions(blockId: string): void {
  if (!source || blockSubscriptions.has(blockId)) return;

  const notify = () => {
    dispatchUpdate(blockId);
    const docId = scheduleDocIds.get(blockId);
    if (docId) refreshAutomationSchedule(blockId, docId);
  };
  blockSubscriptions.set(blockId, [
    source.logging.getLogs$(blockId).subscribe(notify),
    source.logging.getActiveLog$(blockId).subscribe(notify),
    source.execution.getState$(blockId).subscribe(notify),
  ]);
}

function requestHistory(blockId: string): void {
  if (!source || historyRequested.has(blockId)) return;

  if (!canAttemptBackendRequest()) {
    // Offline: defer without marking as done — flushed on availability.
    pendingHistory.add(blockId);
    return;
  }

  historyRequested.add(blockId);
  pendingHistory.delete(blockId);
  source.logging.refreshLogs(blockId, source.getWorkspaceId());
}

function flushPendingHistory(): void {
  if (!canAttemptBackendRequest()) return;
  for (const blockId of Array.from(pendingHistory)) {
    requestHistory(blockId);
  }
}

// =============================================================================
// Bridge lifecycle (called by useAutomationRuntimeBridge)
// =============================================================================

export function getAutomationRuntime(
  blockId: string
): AutomationRuntimeSnapshot | null {
  if (!source) return null;

  const logs = source.logging.getLogs$(blockId).value;
  const activeLog =
    source.logging.getActiveLog$(blockId).value ??
    logs.find(
      log => log.status === 'running' || log.status === 'awaiting_external'
    ) ??
    null;
  const state = source.execution.getState$(blockId).value;

  return {
    history: logs.map(mapLog),
    activeRun: buildActiveRun(activeLog, state),
    schedule: schedules.get(blockId) ?? null,
    readinessFacts: readinessFacts.get(blockId) ?? null,
    access: accesses.get(blockId) ?? null,
    heldRows: heldRowCounts.get(blockId) ?? 0,
  };
}

/** Refusals of a person who may not run automations of the workspace. */
const HELD_ROWS_DENIED: ReadonlySet<string> = new Set([
  'AUTOMATION_ACTIVATION_PERMISSION_DENIED',
  'AUTOMATION_ACTIVATION_GUEST_DENIED',
]);

/**
 * Runs or discards the rows the import brake held back, tells the result
 * and reads the automation again (switched on, nothing held).
 */
export async function resolveAutomationHeldRows(
  blockId: string,
  docId: string,
  action: 'run' | 'discard'
): Promise<void> {
  if (!source) return;
  const workspaceId = source.getWorkspaceId();
  const result = await source.execution.resolveHeldRows(
    workspaceId,
    docId,
    blockId,
    action
  );
  if (!result.success && HELD_ROWS_DENIED.has(result.error)) {
    notify.error({
      title: I18n.t(
        'com.streamlain.streamlain.execution.notify.permission-denied-title'
      ),
      message: I18n.t(
        'com.streamlain.streamlain.execution.notify.permission-denied-message'
      ),
    });
  } else if (!result.success) {
    notify.error({
      title: I18n.t('com.streamlain.automation.block.held-rows.failed'),
      message: formatWorkflowErrorMessage(I18n, result.error) ?? result.error,
    });
  } else if (action === 'run') {
    notify.success({
      title: I18n.t('com.streamlain.automation.run-rows.queued.title', {
        runs: String(result.runs),
      }),
      message: I18n.t('com.streamlain.automation.run-rows.queued.message', {
        rows: String(result.rows),
      }),
    });
  } else {
    notify.success({
      title: I18n.t('com.streamlain.automation.block.held-rows.discarded', {
        count: String(result.rows),
      }),
    });
  }
  refreshAutomationSchedule(blockId, docId);
  source.logging.refreshLogs(blockId, workspaceId);
}

export function ensureAutomationRuntime(blockId: string, docId?: string): void {
  if (!source) return;
  attachBlockSubscriptions(blockId);
  requestHistory(blockId);
  if (docId) refreshAutomationSchedule(blockId, docId);
}

export function releaseAutomationRuntime(blockId: string): void {
  const subscriptions = blockSubscriptions.get(blockId);
  if (subscriptions) {
    for (const subscription of subscriptions) {
      subscription.unsubscribe();
    }
    blockSubscriptions.delete(blockId);
  }
  pendingHistory.delete(blockId);
  schedules.delete(blockId);
  scheduleDocIds.delete(blockId);
  readinessFacts.delete(blockId);
  accesses.delete(blockId);
  heldRowCounts.delete(blockId);
}

export function setAutomationRuntimeSource(
  configApi: AutomationConfigApiService,
  logging: LoggingService,
  execution: ExecutionService,
  getWorkspaceId: () => string,
  getReaderId: () => string | null
): void {
  clearAutomationRuntimeSource();
  source = { configApi, logging, execution, getWorkspaceId, getReaderId };

  availabilityUnsubscribe = onBackendAvailable(() => {
    flushPendingHistory();
  });

  window.__automationRuntimeBridge = {
    getRuntime: getAutomationRuntime,
    ensureRuntime: ensureAutomationRuntime,
    releaseRuntime: releaseAutomationRuntime,
    refreshSchedule: refreshAutomationSchedule,
    setSchedule: setAutomationSchedule,
    setReadinessFacts: setAutomationReadinessFacts,
    resolveHeldRows: resolveAutomationHeldRows,
  };
  window.dispatchEvent(new CustomEvent('automation-runtime-ready'));
}

export function clearAutomationRuntimeSource(): void {
  for (const subscriptions of blockSubscriptions.values()) {
    for (const subscription of subscriptions) {
      subscription.unsubscribe();
    }
  }
  blockSubscriptions.clear();
  historyRequested.clear();
  pendingHistory.clear();
  schedules.clear();
  scheduleDocIds.clear();
  accesses.clear();
  heldRowCounts.clear();
  scheduleRequests.clear();
  availabilityUnsubscribe?.();
  availabilityUnsubscribe = null;
  source = null;
  delete window.__automationRuntimeBridge;
}
